import { AlertTriangle, Clock, Settings, RefreshCw } from 'lucide-react'
import { Card, CardContent } from '@/presentation/components/ui/card'
import { Button } from '@/presentation/components/ui/button'
import { Badge } from '@/presentation/components/ui/badge'

interface UnderMaintenanceProps {
  onRetry?: () => void
}

/**
 * UnderMaintenance Component
 *
 * Displays a maintenance page when the system is under maintenance.
 * Activated by the VITE_MAINTENANCE_MODE environment variable.
 * Designed for Kubernetes deployments where maintenance mode
 * can be activated by redeploying with the environment flag.
 */
export function UnderMaintenance({ onRetry }: UnderMaintenanceProps) {
  // Get maintenance configuration from environment variables
  const title =
    import.meta.env.VITE_MAINTENANCE_TITLE || 'Sistema em Manutenção'
  const message =
    import.meta.env.VITE_MAINTENANCE_MESSAGE ||
    'Estamos realizando melhorias no sistema para oferecer uma melhor experiência. Voltaremos em breve.'
  const estimatedTime = import.meta.env.VITE_MAINTENANCE_ESTIMATED_TIME
  const contactEmail = import.meta.env.VITE_MAINTENANCE_CONTACT_EMAIL
  const showRetry = import.meta.env.VITE_MAINTENANCE_SHOW_RETRY !== 'false'

  const handleRetry = () => {
    if (onRetry) {
      onRetry()
    } else {
      window.location.reload()
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-md mx-auto shadow-lg border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <CardContent className="p-8 text-center space-y-6">
          {/* Animated maintenance icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 bg-greenGarupa/30 dark:bg-greenGarupa/30 rounded-full flex items-center justify-center">
                <Settings
                  className="w-10 h-10 text-greenGarupa dark:text-greenGarupa"
                  style={{
                    animation: 'spin 3s linear infinite',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Status badge */}
          <Badge
            variant="secondary"
            className="bg-greenGarupa/30 text-greenGarupa dark:bg-greenGarupa/30 dark:text-greenGarupa"
          >
            <AlertTriangle className="w-3 h-3 mr-1" />
            Manutenção em Andamento
          </Badge>

          {/* Title */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {title}
            </h1>
          </div>

          {/* Message */}
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {message}
          </p>

          {/* Estimated time (if provided) */}
          {estimatedTime && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4" />
              <span>Tempo estimado: {estimatedTime}</span>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {showRetry && (
              <Button
                onClick={handleRetry}
                className="w-full bg-greenGarupa hover:bg-greenGarupa/60 text-white"
                variant="default"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Tentar Novamente
              </Button>
            )}

            {contactEmail && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Em caso de urgência, entre em contato:{' '}
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {contactEmail}
                </a>
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
